import { createColumnHelper } from '@tanstack/react-table'
import { INote } from 'models/contact_companies'
import { useAuth } from '../../../context/authentication/useAuth'
import { API_HOST } from 'utils/http-client'
import { AFbLink } from 'components/FbUI/FbLink/styles'
import { Editor } from 'components/Editor/Editor'
import { JsonIntoUl } from 'components/json-into-ul'
import { ActionButtons } from '../../Buttons/ActionButtons'
import { BiExpandAlt } from 'react-icons/bi'
import { NOTES_COLUMNS } from './notesColumnInfo'
import { createTableColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { usePreferences } from 'context/preferences/PreferencesContext'

interface UseNotesTableColumnsProps {
  setActiveNote: (noteData: { note: INote; isViewing?: boolean } | null) => void
  handleNoteDelete: (noteId: number) => Promise<void>
}

export const useNotesTableColumns = ({
  setActiveNote,
  handleNoteDelete,
}: UseNotesTableColumnsProps) => {
  const { user: me } = useAuth()
  const { preferences } = usePreferences()
  const tooltips: Record<string, unknown> = preferences?.tooltips || {}

  const columnHelper = createColumnHelper<INote>()
  const notesColumnHelper = createTableColumnHelper({
    columns: NOTES_COLUMNS,
    tooltips,
  })

  return [
    columnHelper.accessor('user', {
      ...notesColumnHelper.retrieveColumn('user'),
      cell: (info) => {
        const row = info.row.original
        return !row.user
          ? `${me?.company.name} User`
          : row.user?.first_name + ' ' + row.user?.last_name
      },
      size: 150,
    }),
    columnHelper.accessor('content', {
      ...notesColumnHelper.retrieveColumn('content'),
      cell: (info) => {
        const value = info.getValue()
        if (!value) return '--'

        return (
          <JsonIntoUl
            value={value}
            fallback={
              <Editor
                value={value}
                readOnly={true}
                disableToolbar={true}
                borderless={true}
              />
            }
          />
        )
      },
    }),
    columnHelper.accessor('attachments', {
      ...notesColumnHelper.retrieveColumn('attachments'),
      cell: (info) => {
        const attachments = info.getValue()
        if (!attachments || attachments.length === 0) return '--'
        return (
          <div className="flex flex-col">
            {attachments?.map((attachment) => {
              const href = attachment?.file?.startsWith('/')
                ? `${API_HOST}${attachment.file}`
                : attachment.file
              return (
                <AFbLink
                  key={attachment.id}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                >
                  {attachment.original_filename}
                </AFbLink>
              )
            })}
          </div>
        )
      },
      size: 200,
    }),
    columnHelper.accessor('created', {
      ...notesColumnHelper.retrieveColumn('created'),
      cell: (info) => {
        const date = info.getValue()
        if (!date) return '-'
        return new Date(date).toLocaleDateString()
      },
      size: 120,
    }),
    columnHelper.accessor('modified', {
      ...notesColumnHelper.retrieveColumn('modified'),
      cell: (info) => {
        const date = info.getValue()
        if (!date) return '-'
        return new Date(date).toLocaleDateString()
      },
      size: 120,
    }),
    columnHelper.display({
      ...notesColumnHelper.retrieveColumn('actions'),
      meta: {
        isEditable: false,
        isLastColumn: true,
        rightAlign: true,
      },
      cell: (info) => {
        const note = info.row.original
        const noteId = note.id
        return (
          <ActionButtons
            className={'justify-start'}
            onDelete={() => handleNoteDelete(noteId)}
            onEdit={() => setActiveNote({ note })}
            onSee={() => setActiveNote({ note, isViewing: true })}
            seeIcon={BiExpandAlt}
          />
        )
      },
      size: 100,
    }),
  ]
}
